import { IconInfoCircle, IconLoader2 } from "@tabler/icons-react";

import type { CpuCapacity } from "../../api/types";
import {
  fleetSpendNote,
  RESERVABLE_NOT_IDLE_NOTE,
  summarizeCpuFleet,
  summarizeCpuMachine,
  summarizeCpuPool,
} from "../../lib/cpu";
import { formatElapsed, pluralize } from "../../lib/format";
import { CoresMeter } from "./CoresMeter";

interface CpuCapacitySectionProps {
  cpu: CpuCapacity;
}

/**
 * The CPU fleet as it really is: pool by pool, machine by machine, in cores.
 *
 * Two things make this different from the GPU fleet section, and both are the
 * contract's doing. It renders a quantity per machine rather than a device per
 * machine, because a core has no identity to render. And it has one state the
 * GPU screen does not: when the console could not make the cluster-wide read
 * that defines a free core, **no count is shown at all**. That fallback is a
 * mild over-report for GPUs and a fatal one for cores — it drops every daemon
 * out of the sum, so a full machine would report itself empty and a run would
 * be placed on it and wait forever.
 *
 * Two smaller states are the same honesty in another direction. The list of
 * machines is capped by the server, so `nodes_omitted` is rendered rather than
 * dropped — a list of 24 machines out of 91 with no indication is invented
 * data. And `shrink_paused_reason` is the sentence a pass writes when it
 * refuses to hand machines back on a fleet it could not read: the counts stay
 * good, the money keeps running, and only that sentence says so.
 *
 * Args:
 *     cpu: The CPU half of the measured capacity payload.
 *
 * Returns:
 *     The CPU fleet section.
 */
export function CpuCapacitySection({
  cpu,
}: CpuCapacitySectionProps): JSX.Element {
  const summary = summarizeCpuFleet(cpu);
  const spend = fleetSpendNote(cpu);
  const poolFailures = cpu.pools.filter((pool) => pool.error !== null);

  return (
    <section className="section" aria-labelledby="cpu-capacity-title">
      <div className="section__head">
        <h2 className="section__title" id="cpu-capacity-title">
          The CPU machines
        </h2>
        {summary ? <span className="section__meta">{summary}</span> : null}
      </div>

      {!cpu.measured ? (
        <p className="notice" role="status">
          <IconInfoCircle size={14} aria-hidden="true" />
          <span>
            {cpu.reason ??
              "The console could not read what every machine has promised to " +
                "its work, and free cores cannot be worked out without that."}{" "}
            So no core count is shown here — not even a rough one. Counting
            only part of what a machine is running would report a full machine
            as empty, and a run started on that answer would wait forever. No
            run can be submitted until this read works again.
          </span>
        </p>
      ) : null}

      {cpu.pools.length > 0 ? (
        <ul className="pool-list">
          {cpu.pools.map((pool) => (
            <li key={pool.pool_id} className="pool-line">
              <span className="pool-line__shape">
                {pool.cores_per_node}-core machines
              </span>
              <span className="pool-line__name mono">{pool.name ?? "—"}</span>
              <span className="pool-line__state">
                {pool.error ?? summarizeCpuPool(pool)}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {poolFailures.length > 0 ? (
        <p className="notice">
          <IconInfoCircle size={14} aria-hidden="true" />
          <span>
            {poolFailures.length === 1
              ? "One CPU machine pool cannot be used, for the reason beside it. No machine is started there."
              : "Some CPU machine pools cannot be used, for the reasons beside them. No machine is started there."}
          </span>
        </p>
      ) : null}

      {cpu.machines_requested !== null &&
      cpu.machines_running !== null &&
      cpu.machines_max !== null ? (
        <p className="capacity__note">
          {/* The count beside the ceiling is the one the ceiling is about:
              `machines_requested` is what Scaleway was asked for, booting
              machines included, and it is what a refusal counted. Both are
              named, because the gap between them is machines being created —
              which is most of a large fan-out's first minutes. */}
          {pluralize(cpu.machines_requested, "CPU machine")} asked for,{" "}
          {cpu.machines_running} of them running the work; the rest are still
          being created.{" "}
          {/* MAX_CPU_MACHINES. Said as what it is — a guard on the fleet so a
              runaway loop cannot buy machines without end — because a number
              on a screen with no explanation reads as somebody's ration. */}
          The console never runs more than {cpu.machines_max} at once: that is
          a guard on the fleet's spending, not a share anybody is given.
        </p>
      ) : null}

      {/* What the fleet costs, never what anybody owes: this console keeps no
          per-person total and no screen may build one. */}
      {spend !== null ? <p className="capacity__note">{spend}</p> : null}

      {cpu.shrink_paused_reason !== null ? (
        // The server's sentence, verbatim: the console is holding the fleet at
        // its current size because it could not read it, and held machines
        // bill exactly like needed ones. Null nearly always, which is why this
        // is a notice rather than a permanent line — and why it says nothing
        // about the counts above, which are fine.
        <p className="notice" role="status">
          <IconInfoCircle size={14} aria-hidden="true" />
          <span>{cpu.shrink_paused_reason}</span>
        </p>
      ) : null}

      {cpu.nodes.length === 0 ? (
        // Only when the machines were actually read. Without that read the
        // list is empty because nothing was counted, not because nothing is
        // running, and "no machine is running right now" would be a fact the
        // console does not have — beside a notice saying so.
        cpu.measured ? (
          <p className="capacity__empty">
            No CPU machine is running right now — that is normal, not broken.
            Submit a run and one is started for you
            {cpu.node_start_eta_minutes !== null
              ? `, usually in about ${cpu.node_start_eta_minutes} min`
              : ". How long a CPU machine takes to start has never been timed here, so the console does not say"}
            ; machines nobody uses are given back.
          </p>
        ) : null
      ) : (
        <ul className="node-list">
          {cpu.nodes.map((node, index) => (
            <li
              key={node.name ?? `starting-${index}`}
              className="node-row"
              data-status={node.status}
            >
              <div className="node-row__id">
                <span className="node-row__shape">
                  {node.cores_total !== null
                    ? `${node.cores_total}-core machine`
                    : "CPU machine"}
                </span>
                <span
                  className="mono node-row__name truncate"
                  title={node.name ?? "New machine"}
                >
                  {node.name ?? "New machine"}
                </span>
                {node.instance_type ? (
                  <span className="node-row__type mono">
                    {node.instance_type}
                  </span>
                ) : null}
              </div>

              {node.status === "provisioning" ? (
                // A machine Scaleway is still creating has no cores yet, which
                // is not the same fact as "the console could not read them" —
                // and that difference is the whole point of the honesty pair.
                // An element stays in this cell rather than nothing: the row
                // is a three-column grid, and dropping the middle child would
                // slide the sentence beside it out of line with its
                // neighbours.
                <p className="util__unknown">
                  No cores yet — this machine is still being created.
                </p>
              ) : (
                <CoresMeter
                  used={node.cores_used}
                  total={node.cores_total}
                  label={node.name ?? "this machine"}
                />
              )}

              <div className="node-row__text">
                {node.status === "provisioning" ? (
                  <p className="node-row__starting">
                    <IconLoader2 size={14} aria-hidden="true" className="spin" />
                    Starting
                    {node.created_at
                      ? ` — asked for ${formatElapsed(node.created_at)} ago`
                      : " — just asked for"}
                    {cpu.node_start_eta_minutes !== null
                      ? `, usually ready in about ${cpu.node_start_eta_minutes} min.`
                      : ". How long these take has never been timed here."}
                  </p>
                ) : (
                  <p className="node-row__summary">
                    {summarizeCpuMachine(node)}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {cpu.nodes_omitted > 0 ? (
        // The list is capped; saying so is what keeps it from being invented
        // data. The order is the contract's — machines holding work first — so
        // what is missing here is the empty end of the fleet, and the counts
        // above already covered every machine of it.
        <p className="capacity__note">
          …and {pluralize(cpu.nodes_omitted, "more machine")}, not listed here.
          The machines with work on them come first, so what is left out is the
          empty end of the fleet; the numbers above count all of them.
        </p>
      ) : null}

      {cpu.measured ? (
        <p className="capacity__note">{RESERVABLE_NOT_IDLE_NOTE}</p>
      ) : null}
    </section>
  );
}
